/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { AsyncPipe } from "@angular/common";
import { Component, EventEmitter, inject, Input, Output } from "@angular/core";
import { FormBuilder, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import { BehaviorSubject, first, from, map, Observable, of, startWith, Subject } from "rxjs";
import { FormActionsComponent, FormComponent, FormInputComponent, FormPasswordInputComponent, ModalComponent, requiredValidator } from "typedb-cloud-framework";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { SnackbarService } from "../../service/snackbar.service";
import { IdentityAuthService } from "../../service/authentication/identity-auth.service";
import { DialogResult } from "../../service/dialog.service";
import { MFAResolveSession } from "../../service/authentication/google-identity-backend.service";
import { ApplicationState } from "../../service/application-state.service";

const AUTHENTICATION_FAILED = "Authentication failed, please try again";
export type ReauthStage = "verify-identity" | "reauthenticate" | "complete";

@Component({
    selector: "tp-reauthenticate",
    templateUrl: "./reauthenticate.component.html",
    styleUrl: "./reauthenticate.component.scss",
    standalone: true,
    imports: [
        AsyncPipe, FormInputComponent, FormActionsComponent, FormsModule, ReactiveFormsModule,
        FormComponent, MatFormFieldModule, MatInputModule, FormPasswordInputComponent, FormActionsComponent
    ],
})
export class ReauthenticateComponent {
    dialogRef: MatDialogRef<ReauthenticateComponent, DialogResult> = inject(MatDialogRef);
    formBuilder = inject(FormBuilder);
    snackbar = inject(SnackbarService);
    auth = inject(IdentityAuthService);
    app = inject(ApplicationState);

    @Input({ required: true }) reauthAction!: string;
    @Input({ required: true }) isSubmitting$ = new Subject<boolean>();

    readonly reauthForm = this.formBuilder.nonNullable.group({
        email: ["", [requiredValidator]],
        password: ["", [requiredValidator]],
    });

    mfaResolveSession$ = new BehaviorSubject<MFAResolveSession | null>(null);
    readonly mfaForm = this.formBuilder.nonNullable.group({ mfaCode: ["", [requiredValidator]] })

    @Output() reauthStageChanged = new EventEmitter<ReauthStage>()

    constructor(
    ) {
        this.reauthStageChanged.emit("reauthenticate");

        this.auth.currentUserStateChanges.pipe(first(user => !!user)).subscribe(user => {
            if (!this.reauthForm.value.email && !!user.email) this.reauthForm.patchValue({ email: user.email });
        });
    }

    microsoftReauth() {
        this.processReauth(this.auth.microsoftLogin());
    }

    googleReauth() {
        this.processReauth(this.auth.googleLogin());
    }

    githubReauth() {
        this.processReauth(this.auth.githubLogin());
    }

    mfaReauth(mfaResolveSession: MFAResolveSession) {
        this.processReauth(from(mfaResolveSession.signInWithCode(this.mfaForm.value.mfaCode!)));
    }

    submitReauth() {
        this.processReauth(this.auth.signIn(this.reauthForm.value.email!, this.reauthForm.value.password!))
    }

    processReauth(reauth: Observable<unknown>) {
        this.isSubmitting$.next(true);
        reauth.subscribe({
            next: () => {
                this.isSubmitting$.next(false);
                this.reauthStageChanged.emit("complete");
            },
            error: (err) => {
                this.isSubmitting$.next(false);
                if (err?.code === 'auth/multi-factor-auth-required') {
                    this.reauthStageChanged.next("verify-identity");
                    this.mfaResolveSession$.next(this.auth.startMFAResolveSession(err));
                }
                else if (err?.code === "auth/invalid-verification-code") this.snackbar.errorPersistent(
                    "Invalid MFA verification code. Please try again."
                );
                else if (err?.code !== "auth/popup-closed-by-user") this.snackbar.errorPersistent(err?.message || err?.toString() || AUTHENTICATION_FAILED);
            }
        });
    }
}
